import { ActivatedRoute, NavigationEnd, Router } from '@angular/router'
import { Component, OnDestroy, OnInit } from '@angular/core'
import { Subject, filter, takeUntil } from 'rxjs'

import { GRUPOS_POR_ATENDER, GrupoChip, grupoValido } from '@modules/private/payment/data/constants/grupos-por-atender'
import { ApiPaymentService } from '@modules/private/payment/data/services/api/api-payment.service'
import { pagosPorAtender } from '@modules/private/payment/data/constants/pagos-por-atender'
import { CobranzaMonthlySummary } from '../data/interfaces/monthly-summary.interface'
import { CobranzaShellService } from '../data/services/view/cobranza-shell.service'
import { InvoicesComponent } from '@modules/private/invoices/invoices.component'
import { ApiCobranzaService } from '../data/services/api/api-cobranza.service'
import { AuthService } from '@data/services/modules/auth/auth.service'

/** Las vistas de Cobranza. Cada una es una RUTA, no estado en memoria. */
export type CobranzaView = 'dinero' | 'comprobantes' | 'central' | 'cartera' | 'avance' | 'conciliacion' | 'atender'

/** Lo que hace falta para pintar y gatear una vista. */
interface ViewDef {
  key: CobranzaView
  label: string
  icon: string
  path: string
  /** El permiso que la abre. Cada vista lleva el suyo en su propia ruta. */
  perm: [string, string]
}

/**
 * El centro de cobranza: el dinero del día de la arrendadora y los comprobantes que lo respaldan.
 *
 * **Una tarjeta, dos barras de control.** El encabezado con el mes y las acciones, el embudo —que es
 * dato, no control—, la barra con la vista y los filtros, y la tabla.
 *
 * **Es un CASCARÓN, y eso es deliberado.** Las vistas cuelgan de `<router-outlet>` en vez de estar
 * puestas con `*ngIf`. Con los `*ngIf`, cambiar de vista navegaba a una ruta hermana y Angular
 * destruía y recreaba esta pantalla entera: el encabezado, el embudo y las fichas volvían a montarse
 * y a pedir sus datos para acabar enseñando exactamente lo mismo. Ahora el cascarón sobrevive y lo
 * único que se cambia es el cuerpo, que es lo único que cambia.
 *
 * **El mes manda sobre las dos vistas** y por eso vive aquí. Baja por `CobranzaShellService` y no por
 * `@Input`: un componente enrutado no recibe entradas de su padre.
 *
 * Cada ruta hija lleva su propio par `module`/`permission`, que es el arreglo estructural del B-04 —
 * el candado deja de depender de que el componente se acuerde de comprobarlo.
 */
@Component({
  selector: 'app-cobranza-page',
  templateUrl: './cobranza-page.component.html',
  styleUrls: ['./cobranza-page.component.scss'],
})
export class CobranzaPageComponent implements OnInit, OnDestroy {
  readonly views: ViewDef[] = [
    { key: 'dinero', label: 'Dinero', icon: 'fa-solid fa-money-bill-transfer', path: '/cobranza/dinero', perm: ['payments', 'view'] },
    { key: 'comprobantes', label: 'Comprobantes', icon: 'fa-solid fa-file-invoice-dollar', path: '/cobranza/comprobantes', perm: ['invoices', 'view'] },
    //* CENTRAL es el libro de Contabilidad, derivado. Su par es `invoices`/`get-all`, el mismo que
    //* abre su endpoint: con `view` la pestaña aparecería para quien recibe un 401 al entrar.
    { key: 'central', label: 'CENTRAL', icon: 'fa-solid fa-table-list', path: '/cobranza/central', perm: ['invoices', 'get-all'] },
    { key: 'cartera', label: 'Cartera', icon: 'fa-solid fa-wallet', path: '/cobranza/cartera', perm: ['wallet', 'get-all'] },
    { key: 'avance', label: 'Avance', icon: 'fa-solid fa-chart-line', path: '/cobranza/avance', perm: ['payments', 'view'] },
    //! `reconcile` y no `view`: esta vista enseña CLABEs de clientes. Y no `adjust`, que es la
    //! cirugía de pagos — Contabilidad concilia sin poder anular ni condonar.
    { key: 'conciliacion', label: 'Conciliación', icon: 'fa-solid fa-building-columns', path: '/cobranza/conciliacion', perm: ['payments', 'reconcile'] },
  ]

  view: CobranzaView = 'dinero'
  summary: CobranzaMonthlySummary | null = null
  loadingSummary = false

  /** El mes que manda. Se guarda como Date porque lo edita un `p-calendar`. */
  month: Date = new Date()

  /** Cuántos pagos necesitan intervención. El aviso del encabezado solo existe si es mayor que cero. */
  attentionCount = 0

  /**
   * El resumen de «por atender», con los conteos y los importes de los cuatro grupos.
   *
   * Vive aquí y no en la bandeja porque las fichas de grupo se pintan en la **barra de la vista**,
   * junto al segmentado: eran cuatro tarjetas de 90 px que empujaban la tabla fuera de la pantalla
   * para decir cuatro números. Y esta pantalla ya pedía este mismo endpoint para el aviso del
   * encabezado, así que no cuesta una consulta más.
   */
  attentionResumen: Record<string, number> | null = null

  readonly grupos: GrupoChip[] = GRUPOS_POR_ATENDER

  /** Qué grupo está abierto. Viaja en la dirección para poder compartir «los remanentes de hoy». */
  grupo = grupoValido(null)

  /** El asistente de carga, en su modal. */
  showBatch = false
  /** El lote que hay que recuperar, cuando se entra por `?carga=<uploadBatch>`. */
  batchToRecover = ''

  /**
   * La vista de Comprobantes, cuando está montada.
   *
   * Sus tres controles —Central de Facturas, Reporte mensual y Carga masiva— vivían en un
   * encabezado propio que aquí no se pinta, así que quedaron **inalcanzables** (H-01). Se recuperan
   * en la barra de la vista, que es su sitio: el encabezado es de la pantalla, la barra es de la
   * vista. La referencia la da el outlet al activarla y no un `@ViewChild`: al hijo lo crea el
   * enrutador y no está en esta plantilla.
   */
  invoices: InvoicesComponent | null = null

  private readonly destroy$ = new Subject<void>()

  constructor(
    public readonly authService: AuthService,
    private readonly shell: CobranzaShellService,
    private readonly api: ApiCobranzaService,
    private readonly apiPayment: ApiPaymentService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    //* El mes vive en un servicio de raíz para poder bajarlo a las vistas enrutadas, así que
    //* sobrevive a salir de la pantalla. Al entrar se vuelve al mes en curso: nadie pidió seguir en
    //* julio una hora después.
    this.shell.reset()
    this.month = this.shell.month

    this.readChildRoute()
    //* La vista sale de la ruta HIJA, y al navegar entre hermanas este componente ya NO se recrea:
    //* hay que volver a leerla en cada navegación o el segmentado se queda en la anterior.
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.readChildRoute())

    this.route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe((q) => {
      const carga = q.get('carga')
      this.showBatch = !!carga
      this.batchToRecover = carga && carga !== 'nuevo' ? carga : ''
      this.grupo = grupoValido(q.get('grupo'))
    })

    //* Una vista escribió algo que cambia las cifras del mes: se recargan. Sin esto, cargar cuatro
    //* facturas dejaba «Facturado $0.00» arriba — una cifra falsa es peor que ninguna.
    this.shell.refresh$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.loadSummary()
      this.loadAttentionCount()
    })

    this.loadSummary()
    this.loadAttentionCount()
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  /** Las vistas que este rol puede abrir. La que no puede, no se pinta ni se monta. */
  get visibleViews(): ViewDef[] {
    return this.views.filter((v) => this.authService.hasModulePermission(v.perm[0], v.perm[1]))
  }

  /** El mes en `YYYY-MM`, que es como lo pide la API. */
  get monthParam(): string {
    const d = this.month || new Date()

    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
  }

  onMonthChange(): void {
    //* Se publica para las vistas —Comprobantes lo escucha— y se recarga el embudo, que es lo que
    //* esta pantalla posee.
    this.shell.setMonth(this.month)
    this.loadSummary()
  }

  /** Guarda la vista recién montada: de ahí salen sus controles propios. */
  onViewActivated(componente: unknown): void {
    this.invoices = componente instanceof InvoicesComponent ? componente : null
  }

  goTo(view: ViewDef): void {
    if (view.key === this.view) return

    //* Se NAVEGA. Cambiar de vista tiene que quedar en la barra de direcciones y en el historial: es
    //* lo que permite guardar una vista en marcadores y que «atrás» deshaga el cambio.
    this.router.navigate([view.path])
  }

  openAttention(): void {
    this.router.navigate(['/cobranza/por-atender'])
  }

  /** Cuántos pagos hay en un grupo, y cuánto dinero suman. */
  conteo(g: GrupoChip): number {
    return this.attentionResumen?.[g.key] ?? 0
  }

  monto(g: GrupoChip): number {
    return this.attentionResumen?.[g.montoKey] ?? 0
  }

  /**
   * Cambia de grupo por la DIRECCIÓN, no por una variable.
   *
   * Así «los remanentes» es un enlace que se pega en un mensaje, «atrás» deshace el cambio, y la
   * bandeja —que es un componente enrutado— se entera sin que nadie le pase nada.
   */
  verGrupo(g: GrupoChip): void {
    if (g.key === this.grupo) return
    this.router.navigate([], { relativeTo: this.route, queryParams: { grupo: g.key }, queryParamsHandling: 'merge' })
  }

  // ── El asistente de carga ──

  /**
   * Abre el asistente **sin salir de Cobranza**.
   *
   * El modal se enciende por la DIRECCIÓN y no por una bandera suelta: así «atrás» lo cierra, un
   * lote a medias se puede compartir por enlace, y recargar la pestaña vuelve a donde estaba — que
   * era justo lo que compraba la ruta de pantalla completa.
   */
  openBatch(): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: { carga: 'nuevo' }, queryParamsHandling: 'merge' })
  }

  closeBatch(): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: { carga: null }, queryParamsHandling: 'merge' })
    //* Al cerrar, el dinero del mes pudo cambiar: el asistente registra y aplica pagos.
    this.loadSummary()
    this.loadAttentionCount()
  }

  /**
   * Los pagos ya existen: la huella del lote entra en la dirección **en ese mismo instante**.
   *
   * Se reemplaza la entrada del historial en vez de apilar una nueva: «atrás» tiene que cerrar el
   * asistente, no devolverlo al estado de antes de registrar — que ya no existe.
   */
  onBatchRegistered(uploadBatch: string): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: { carga: uploadBatch }, queryParamsHandling: 'merge', replaceUrl: true })
  }

  /** Cerrar por fuera (Escape) también tiene que limpiar la dirección, o al recargar reabre. */
  onBatchClosed(): void {
    if (this.route.snapshot.queryParamMap.get('carga')) this.closeBatch()
  }

  // ── Los controles de Comprobantes, recuperados (H-01) ──

  openPendingExport(): void {
    this.invoices?.openPendingExport()
  }

  downloadMonthlyReport(): void {
    this.invoices?.downloadMonthlyReport()
  }

  openInvoiceUpload(): void {
    if (this.invoices) this.invoices.showUpload = true
  }

  /** Qué vista está activa, leída de la ruta hija. */
  private readChildRoute(): void {
    let r = this.route.firstChild
    while (r?.firstChild) r = r.firstChild
    this.view = (r?.snapshot.data?.['view'] as CobranzaView) ?? 'dinero'
  }

  /**
   * El embudo del mes. Si falla, la franja se esconde y la tabla se queda: perder el resumen es
   * molesto, no poder trabajar sería peor.
   */
  private loadSummary(): void {
    this.loadingSummary = true
    this.api.monthlySummary(this.monthParam).subscribe((r) => {
      this.loadingSummary = false
      this.summary = r.error ? null : r.data
    })
  }

  /**
   * El conteo de «por atender», al cargar la pantalla.
   *
   * Antes se pedía al ENTRAR a su pestaña, así que el globo solo aparecía después de que ya habías
   * ido a mirar: el recordatorio no recordaba nada. Aquí carga con la pantalla, que es cuando sirve.
   */
  private loadAttentionCount(): void {
    if (!this.authService.hasModulePermission('payments', 'adjust')) return

    this.apiPayment.getNeedingAttention().subscribe((r) => {
      //! La cuenta sale de `pagosPorAtender`, que es la MISMA que usa el globo de la bandeja. Aquí
      //! estaba escrita a mano y sumaba además los posibles duplicados: el aviso decía «2 por
      //! atender» y al entrar solo había 1.
      this.attentionResumen = r.error ? null : (r?.data?.resumen ?? null)
      this.attentionCount = r.error ? 0 : pagosPorAtender(r?.data?.resumen)
    })
  }
}
